import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShoppingBag, Plus, Lock, Clock, AlertTriangle, ZoomIn } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AllergenBadges } from "./AllergenComponents";
import { MediaPreviewLightbox } from "./MediaPreviewLightbox";
import { resolveMedia } from "../../pages/PublicMenuPage";
import { renderDescription } from "./ProductDescription";

export default function ProductDetailModal({
  open, onOpenChange, product, isUnavailable, isDark, cardBg, textMain, textSub,
  pc, prC, fmt, hasDiscount, displayOriginalPrice, effectiveDiscount,
  badgeText, availabilityMessage, onAdd, restaurantSlug, autoOpen, openCheckout,
}) {
  const { t } = useTranslation();
  const [selections, setSelections] = useState({});
  const [mediaPreview, setMediaPreview] = useState(null);

  const varGroups = product.variation_groups || [];
  const hasVariations = varGroups.length > 0;

  const getItemQty = (groupId, itemName) => {
    const sel = selections[groupId];
    if (!sel) return 0;
    if (Array.isArray(sel)) { const found = sel.find(s => s.name === itemName); return found ? (found.quantity || 1) : 0; }
    return sel.name === itemName ? (sel.quantity || 1) : 0;
  };

  const getGroupTotalQty = (groupId) => {
    const sel = selections[groupId];
    if (!sel) return 0;
    if (Array.isArray(sel)) return sel.reduce((sum, s) => sum + (s.quantity || 1), 0);
    return sel ? (sel.quantity || 1) : 0;
  };

  const getExtraPrice = () => {
    let extra = 0;
    for (const g of varGroups) {
      const sel = selections[g.id];
      if (sel) { if (Array.isArray(sel)) { for (const s of sel) extra += (s.price || 0) * (s.quantity || 1); } else extra += (sel.price || 0) * (sel.quantity || 1); }
    }
    return extra;
  };

  const totalPrice = product.price + getExtraPrice();

  const canAdd = () => {
    for (const g of varGroups) {
      if (g.required && getGroupTotalQty(g.id) < (g.min_select || 1)) return false;
    }
    return true;
  };

  const changeVariationQty = (groupId, item, maxSelect, delta) => {
    setSelections(prev => {
      const current = prev[groupId];
      if (maxSelect === 1) return { ...prev, [groupId]: delta > 0 ? item : null };
      const arr = Array.isArray(current) ? [...current] : [];
      const existingIdx = arr.findIndex(a => a.name === item.name);
      const totalQty = arr.reduce((sum, a) => sum + (a.quantity || 1), 0);
      if (delta > 0) {
        if (totalQty >= maxSelect) return prev;
        if (existingIdx >= 0) {
          const updated = [...arr]; updated[existingIdx] = { ...updated[existingIdx], quantity: (updated[existingIdx].quantity || 1) + 1 };
          return { ...prev, [groupId]: updated };
        }
        return { ...prev, [groupId]: [...arr, { ...item, quantity: 1 }] };
      } else {
        if (existingIdx < 0) return prev;
        const updated = [...arr];
        const newQty = (updated[existingIdx].quantity || 1) - 1;
        if (newQty <= 0) updated.splice(existingIdx, 1); else updated[existingIdx] = { ...updated[existingIdx], quantity: newQty };
        return { ...prev, [groupId]: updated };
      }
    });
  };

  const handleAdd = () => {
    const selectedItems = [];
    for (const g of varGroups) {
      const sel = selections[g.id];
      if (sel) {
        if (Array.isArray(sel)) selectedItems.push(...sel.map(s => ({ group: g.name, ...s })));
        else selectedItems.push({ group: g.name, ...sel });
      }
    }
    onAdd({ ...product, selectedVariations: selectedItems, finalTotal: totalPrice });
    onOpenChange(false);
    setSelections({});
  };

  const handleBuyNow = () => { handleAdd(); openCheckout?.(); };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-lg p-0 flex flex-col max-h-[92vh] w-[calc(100vw-2rem)] sm:w-auto"
          style={{ backgroundColor: cardBg, color: textMain }}
          data-testid="product-detail-modal"
          onPointerDownOutside={(e) => { if (mediaPreview) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (mediaPreview) e.preventDefault(); }}
          onInteractOutside={(e) => { if (mediaPreview) e.preventDefault(); }}
        >
          {(product.image_url || product.video_url) && (
            <div className={`w-full h-48 bg-gray-100 relative shrink-0 ${isUnavailable ? "grayscale-[50%] opacity-70" : ""}`}>
              {product.media_type === "video" && product.video_url
                ? <video src={resolveMedia(product.video_url)} className="w-full h-full object-cover"
                    controls muted autoPlay playsInline preload="auto" crossOrigin="anonymous"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                : <img src={resolveMedia(product.image_url)} alt={product.name}
                    className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              }
            </div>
          )}

          <div className="p-5 space-y-4 overflow-y-auto flex-1 pb-24">
            <div>
              <h2 className="text-xl font-bold" style={{ color: textMain }}>{product.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                {hasDiscount && <span className="text-lg line-through" style={{ color: textSub }}>{fmt(displayOriginalPrice + getExtraPrice())}</span>}
                <p className={`text-2xl font-bold ${hasDiscount ? "text-red-600" : ""}`} style={!hasDiscount ? { color: isUnavailable ? textSub : prC } : {}}>{fmt(totalPrice)}</p>
                {hasDiscount && effectiveDiscount > 0 && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">-{effectiveDiscount}%</span>}
              </div>
            </div>

            {isUnavailable && (
              <div className="flex items-start gap-2 rounded-lg px-3 py-2.5 border"
                style={{ backgroundColor: isDark ? 'rgba(251,191,36,0.1)' : '#fffbeb', borderColor: isDark ? 'rgba(251,191,36,0.3)' : '#fde68a' }}
                data-testid="modal-unavailable-notice">
                <Lock className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                <div>
                  <p className="text-sm font-semibold text-amber-700">{badgeText}</p>
                  {availabilityMessage && <p className="text-xs text-amber-600 mt-0.5">{availabilityMessage}</p>}
                </div>
              </div>
            )}

            {product.description && renderDescription(product.description, isDark)}

            {product.preparation_time > 0 && !isUnavailable && (
              <div className="flex items-center gap-1.5 text-xs" style={{ color: textSub }} data-testid={`detail-prep-time-${product.id}`}>
                <Clock className="w-3.5 h-3.5" />
                <span>Tempo de preparo: ~{product.preparation_time} min</span>
              </div>
            )}

            {varGroups.map(group => (
              <div key={group.id} data-testid={`variation-group-${group.id}`}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius:'8px', marginBottom:'10px', backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : `${pc}18`, borderLeft: `5px solid ${pc}` }}>
                  <p style={{ margin:0, fontSize:'13px', fontWeight:900, textTransform:'uppercase', letterSpacing:'0.13em', color: pc }}>{group.name}</p>
                  <div style={{ display:'flex', gap:'4px' }}>
                    {group.required && <span style={{ fontSize:'10px', backgroundColor:'#fff7ed', color:'#c2410c', padding:'2px 6px', borderRadius:'4px', fontWeight:700 }}>Obrigatorio</span>}
                    {group.max_select > 1 && <span style={{ fontSize:'10px', padding:'2px 6px', borderRadius:'4px', fontWeight:600, backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db', color: textSub }}>Ate {group.max_select}</span>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {(group.items || []).map((item, idx) => {
                    const qty = getItemQty(group.id, item.name);
                    const totalGroupQty = getGroupTotalQty(group.id);
                    const effectiveMax = group.max_select > 1 ? group.max_select : 99;
                    const canIncrease = totalGroupQty < effectiveMax;
                    return (
                      <div key={idx} className="w-full flex items-center gap-3 p-3 rounded-lg border transition"
                        style={qty > 0 ? { borderColor: pc, backgroundColor: `${pc}18` } : { borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#e5e7eb' }}
                        data-testid={`variation-item-${group.id}-${idx}`}>
                        {(() => {
                          const mediaUrl = item.image_url || item.video_url;
                          const mediaType = item.video_url ? "video" : "image";
                          if (!mediaUrl) return null;
                          const resolved = resolveMedia(mediaUrl);
                          return (
                            <div className="relative shrink-0 rounded-xl overflow-hidden" style={{ width: 60, height: 60, cursor: "pointer" }}
                              onPointerDown={(e) => { e.stopPropagation(); setMediaPreview({ url: resolved, type: mediaType }); }}>
                              <img src={resolved} alt={item.name} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 active:opacity-100 transition-opacity" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
                                <ZoomIn className="w-4 h-4 text-white" />
                              </div>
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold block" style={{ color: textMain }}>{item.name}</span>
                          {item.description && <span className="text-xs block mt-0.5" style={{ color: textSub }}>{item.description}</span>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs font-bold shrink-0" style={{ color: pc }}>{item.price > 0 ? `+${fmt(item.price)}` : "Incluido"}</span>
                          <div className="flex items-center gap-1">
                            {qty > 0 && (
                              <button onPointerDown={(e) => { e.stopPropagation(); changeVariationQty(group.id, item, effectiveMax, -1); }}
                                className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xl select-none"
                                style={{ backgroundColor: `${pc}30`, color: pc, touchAction: 'manipulation' }}>−</button>
                            )}
                            {qty > 0 && <span className="text-sm font-bold w-5 text-center" style={{ color: pc }}>{qty}</span>}
                            <button onPointerDown={(e) => { e.stopPropagation(); if (canIncrease) changeVariationQty(group.id, item, effectiveMax, 1); }}
                              disabled={!canIncrease}
                              className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xl disabled:opacity-30 select-none"
                              style={{ backgroundColor: pc, color: '#fff', touchAction: 'manipulation' }}>+</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {product.allergens && product.allergens.length > 0 && !isUnavailable && (
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-semibold uppercase" style={{ color: textSub }}>Alergenos</span>
                </div>
                <AllergenBadges allergens={product.allergens} />
              </div>
            )}
          </div>

          <div className="shrink-0 p-4 border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb', backgroundColor: cardBg }}>
            {product.public_display_only ? (
              <div className="w-full h-12 rounded-md flex items-center justify-center gap-2 font-semibold text-sm bg-orange-50 text-orange-700 border border-orange-200" data-testid="modal-display-only-block">
                <ShoppingBag className="w-4 h-4" />
                {t("publicMenu.inStoreOnly", "Solo consumo en restaurante")}
              </div>
            ) : isUnavailable && !hasVariations ? (
              <div className="w-full h-12 rounded-md flex items-center justify-center gap-2 font-semibold text-sm"
                style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6', color: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }}
                data-testid="modal-unavailable-block">
                <Lock className="w-4 h-4" />
                {badgeText || availabilityMessage || "Indisponível"}
              </div>
            ) : autoOpen && openCheckout ? (
              <button className="w-full h-14 text-white font-black text-base rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                style={{ backgroundColor: pc, boxShadow: `0 4px 18px ${pc}66` }}
                onClick={handleBuyNow} disabled={hasVariations && !canAdd()} data-testid="modal-buy-offer">
                <ShoppingBag className="w-5 h-5" />
                {t("publicMenu.buyThisProduct", "Comprar este produto")} — {fmt(totalPrice)}
              </button>
            ) : (
              <button className="w-full h-12 text-white font-bold text-sm rounded-md disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: pc }} onClick={handleAdd} disabled={hasVariations && !canAdd()} data-testid="modal-add-product">
                <Plus className="w-4 h-4" /> {t("publicMenu.addToOrder", "Adicionar ao pedido")} - {fmt(totalPrice)}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {mediaPreview && (
        <MediaPreviewLightbox url={mediaPreview.url} type={mediaPreview.type} onClose={() => setMediaPreview(null)} />
      )}
    </>
  );
}
